import { Api } from 'laravel-request';

const PROFILE_INDEX_URL = `${process.env.REACT_APP_API_URL}/api/v1/call/profile/index`;

export const PROFILE_FETCH_MAX_ATTEMPTS = 3;
export const PROFILE_RETRY_DELAY_MS = 800;

interface ProfileFetchErrorInterface {
    statusCode?: number;
}

function delay(milliseconds: number): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(resolve, milliseconds);
    });
}

function fetchProfileOnce(): Promise<unknown> {
    return new Promise((resolve, reject) => {
        Api.makeRequest({
            method: 'GET',
            url: PROFILE_INDEX_URL,
            success: (response) => {
                resolve(response);
            },
            error: (_xhr, _responseData, statusCode) => {
                const normalizedStatusCode =
                    typeof statusCode === 'number' && Number.isFinite(statusCode)
                        ? statusCode
                        : undefined;
                reject({ statusCode: normalizedStatusCode } satisfies ProfileFetchErrorInterface);
            },
        });
    });
}

function shouldRetryProfileFetch(statusCode: number | undefined): boolean {
    if (statusCode == null) {
        return true;
    }
    if (statusCode === 401) {
        return true;
    }
    if (statusCode >= 500) {
        return true;
    }
    return false;
}

export async function fetchProfileWithRetry(
    maxAttempts: number = PROFILE_FETCH_MAX_ATTEMPTS
): Promise<unknown> {
    let lastError: ProfileFetchErrorInterface = {};

    for (let attemptIndex = 1; attemptIndex <= maxAttempts; attemptIndex += 1) {
        try {
            return await fetchProfileOnce();
        } catch (caughtError) {
            const profileFetchError = caughtError as ProfileFetchErrorInterface;
            lastError = profileFetchError;
            const isLastAttempt = attemptIndex >= maxAttempts;

            if (!isLastAttempt && shouldRetryProfileFetch(profileFetchError.statusCode)) {
                await delay(PROFILE_RETRY_DELAY_MS * attemptIndex);
                continue;
            }

            throw profileFetchError;
        }
    }

    throw lastError;
}
